import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import { Form as FormEngine, FormDefinition } from '@mylicenseone/forms';
import jsonpath from 'jsonpath';
import { cloneDeepWith, isArray } from 'lodash';
import { Form, FormField } from '../../store/modules/application/types';
import FormPanel from './FormPanel';
import { stripNulls } from '../../utils/utils';
import { ErrorState } from './types';
import { siteActions } from '../../store/actions';

type FormDisplayProps = {
    form: Form;
    record: any;
    handleSave: Function;
    handleSelectedInspector?: Function;
};

const useStyles = makeStyles((theme: Theme) => ({
    containerItem: {
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4),
        },
    },
    container: {},
}));

/**
 * Extract data out of a record into a format that the form can work with.
 */
export function extractFormData(record: any, form: Form): Record<string, any> {
    return _extractFormData(
        record,
        form.sections.flatMap((section) => section.entries),
    );
}

function _extractFormData(record: any, formFields: FormField[]): Record<string, any> {
    let data: any = {};

    formFields.forEach((field) => {
        if (field.fieldMapping?.targetPath) {
            let value = jsonpath.value(record, field.fieldMapping.targetPath);
            if (field.fieldMapping?.targetPath) {
                if (value !== null && value !== undefined) {
                    const nestedFields = field.fields;

                    // For repeatable fields, extract data out of each item in the array.
                    if (field.typeId === 'multiple' && Array.isArray(value) && nestedFields) {
                        value = value.map((item: any) => _extractFormData(item, nestedFields));
                    } else if (
                        field.typeId === 'choices' &&
                        Array.isArray(value) &&
                        field.multiSelect &&
                        field.multiple?.targetPath
                    ) {
                        const valueMultiple = value.map((record: any) =>
                            jsonpath.value(record, field.multiple?.targetPath!),
                        );
                        value = valueMultiple;
                    }
                } else if (!value && field.field === 'inspector' && !!record?.inspector?.license?.id) {
                    value = jsonpath.value(record, '$.inspector');
                }

                !!value && Object.assign(data, { [field.field]: value });
            }
        }
    });
    if (record?._context && !data?._context) {
        data = { ...data, ...{ _context: record._context } };
    }
    return data;
}

/**
 * Transform form data back into the original record format.
 */
export function restoreRecord(record: any, formData: Record<string, unknown>, form: Form): any {
    const updatedRecord = cloneDeepWith(record, (value) => value);

    // Strip off any nulls from the original record.  The apis will reject nulls if the field
    // is supposed to be an array, but since we don't know what fields are supposed to be
    // arrays, strip all the nulls off.  It's still okay for the formData to set a value to
    // null as long as that field is not an array field (likely won't be since formData would
    // just set an array field to an empty array if it has no values).
    stripNulls(updatedRecord);

    form.sections
        .flatMap((section) => section.entries)
        .forEach((entry) => {
            let fieldData: any = formData[entry.field];

            // this doesn't restore repeatable fields.
            if (entry.fieldMapping?.targetPath && entry.typeId !== 'multiple') {
                if (entry.fieldMapping.targetPath === '$.inspector.id' && !!updatedRecord.inspector) {
                    if (typeof fieldData === 'string') {
                        // Remove license record details while changing from 3rd party inspector to regular user
                        updatedRecord.inspector = { id: fieldData };
                    } else {
                        jsonpath.value(updatedRecord, '$.inspector', fieldData);
                    }
                } else jsonpath.value(updatedRecord, entry.fieldMapping.targetPath, fieldData);
            }
            // this updates repeatable fields
            else if (entry.fieldMapping?.targetPath && entry.typeId === 'multiple' && fieldData) {
                // looping through each entered record in the repeatable field
                let newAttribute = fieldData.map((record: any) => {
                    let newObj: any = {};
                    // mapping nested repreatable field values based on the nested field mappings
                    entry.fields?.forEach((field) => {
                        // check targetPath and an element with this field name exists to prevent assertion error
                        if (field.fieldMapping?.targetPath && record[field.field]) {
                            jsonpath.value(newObj, field.fieldMapping?.targetPath, record[field.field]);
                        }
                    });
                    return newObj;
                });

                // update the record at the correct path with the new attribute
                jsonpath.value(updatedRecord, entry.fieldMapping.targetPath, newAttribute);
            }

            if (entry.fieldMapping?.targetPath && entry.multiple?.targetPath) {
                let targetRecord = jsonpath.value(updatedRecord, entry.fieldMapping.targetPath);

                if (isArray(targetRecord)) {
                    const valueMultiple = targetRecord.map((record: any) => {
                        const recordMapping: Record<string, unknown> = {};

                        jsonpath.value(recordMapping, entry.multiple?.targetPath!, record);

                        return recordMapping;
                    });
                    jsonpath.value(updatedRecord, entry.fieldMapping.targetPath, valueMultiple);
                }
            }
        });
    // Removing context from the updatedRecord
    // eslint-disable-next-line
    let { _context, ...newUpdatedRecord } = updatedRecord;

    return newUpdatedRecord;
}

const FormDisplay: FunctionComponent<FormDisplayProps> = (props) => {
    const { form, record, handleSave, handleSelectedInspector } = props;
    const classes = useStyles();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState<Record<string, any>>(extractFormData(record, form));
    const [formErrors, setFormErrors] = useState<Record<string, ErrorState>>({});
    const [formEngine, setFormEngine] = useState<FormEngine>();

    const formErrorsRef = useRef<Record<string, ErrorState>>({});

    useEffect(() => {
        formEngine?.evaluateApplicationData(formData, { validateDefinedOnly: true });
    }, [formEngine, formData]);

    const handleInputChange = (dataKey: any, value: unknown) => {
        setFormData({
            ...formData,
            [dataKey]: value,
        });
    };

    const handlePanelEdit = () => {
        const engine = new FormEngine(form as FormDefinition);

        engine.onValidationChange((field, error, messages) => {
            formErrorsRef.current = {
                ...formErrorsRef.current,
                [field]: {
                    error,
                    messages,
                },
            };

            setFormErrors(formErrorsRef.current);
        });

        setFormEngine(engine);
        setFormErrors({});

        formErrorsRef.current = {};
    };

    const handlePanelSave = async () => {
        await formEngine?.evaluateApplicationData(formData);

        if (formEngine?.applicationHasErrors()) {
            dispatch(
                siteActions.setError({ message: 'Please correct errors and try again', isError: true, errorCode: 0 }),
            );
        } else {
            const updatedRecord = restoreRecord(record, formData, form);
            handleSave(updatedRecord);
        }
    };

    const handleCancel = () => {
        setFormData(extractFormData(record, form));
    };
    return (
        <Grid container className={classes.container} spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={6}>
                {form.sections.map((section, index) => {
                    if (index % 2 === 0) {
                        return (
                            <Grid key={section.name} container spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <FormPanel
                                        formSection={section}
                                        formData={formData}
                                        formErrors={formErrors}
                                        handleEdit={handlePanelEdit}
                                        handleInputChange={handleInputChange}
                                        handleSave={handlePanelSave}
                                        handleCancel={handleCancel}
                                        handleSelectedInspector={handleSelectedInspector}
                                    />
                                </Grid>
                            </Grid>
                        );
                    }
                    return <></>;
                })}
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
                {form.sections.map((section, index) => {
                    if (index % 2 !== 0) {
                        return (
                            <Grid key={section.name} container spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <FormPanel
                                        formSection={section}
                                        formData={formData}
                                        formErrors={formErrors}
                                        handleEdit={handlePanelEdit}
                                        handleInputChange={handleInputChange}
                                        handleSave={handlePanelSave}
                                        handleCancel={handleCancel}
                                        handleSelectedInspector={handleSelectedInspector}
                                    />
                                </Grid>
                            </Grid>
                        );
                    }
                    return null;
                })}
            </Grid>
        </Grid>
    );
};

export default FormDisplay;
